/**
 * @description One file staged in the chat composer, ready to ride along with the next message
 * in the `files` array of the `chat/stream` request body.
 */
export interface ChatAttachment {
  filename: string;
  mediaType: string;
  base64Data: string;
}

/** Matches the existing resume upload cap (`UploadFileCv.tsx`) and stays under the agents service limit. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * @description The only extensions the agents service accepts as chat documents. It re-checks the
 * real content server-side and drops a mismatch *silently*, so this gate is the only thing standing
 * between the user and a file that disappears without an error.
 */
export const ACCEPTED_DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx'];

/**
 * @description Images take a different server path: they are keyed off an `image/*` media type and
 * skip the content sniff that guards documents, then get re-encoded and archived. Kept to the
 * formats that survive that re-encode.
 */
export const ACCEPTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];

/**
 * @description Everything the picker accepts — one list, not one per kind. The user picks a file;
 * which of the two server paths it takes is decided by its media type, never by which control they
 * pressed. `MEDIA_TYPE_BY_EXTENSION` below is what resolves that, so the distinction lives in one
 * place and the UI does not have to ask the user a question it can answer itself.
 */
export const ACCEPTED_ATTACHMENT_EXTENSIONS = [
  ...ACCEPTED_DOCUMENT_EXTENSIONS,
  ...ACCEPTED_IMAGE_EXTENSIONS,
];

const MEDIA_TYPE_BY_EXTENSION: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.webp': 'image/webp',
};

const extensionOf = (filename: string): string => {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
};

export type AttachmentRejection = 'ok' | 'type' | 'size';

/**
 * @description Whether a picked file may be attached. Type is checked before size so a large
 * unsupported file reports the reason the user can act on.
 */
export const validateAttachmentFile = (file: { name: string; size: number }): AttachmentRejection => {
  if (!ACCEPTED_ATTACHMENT_EXTENSIONS.includes(extensionOf(file.name))) return 'type';
  if (file.size > MAX_ATTACHMENT_BYTES) return 'size';
  return 'ok';
};

/**
 * @description Read a picked file into a `ChatAttachment`. The media type is derived from the
 * extension rather than trusting `File.type`, which browsers leave empty for `.docx` often enough
 * to matter.
 */
export const readFileAsAttachment = (file: File): Promise<ChatAttachment> => new Promise(
  (resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the selected file'));
    reader.onload = () => {
      // `readAsDataURL` always yields a string; guard rather than stringify, so an
      // ArrayBuffer could never be coerced into a bogus payload.
      if (typeof reader.result !== 'string') {
        reject(new Error('Could not read the selected file'));
        return;
      }
      // `readAsDataURL` yields "data:<mime>;base64,<payload>" — the server wants the payload alone.
      const base64Data = reader.result.slice(reader.result.indexOf(',') + 1);
      resolve({
        filename: file.name,
        mediaType: MEDIA_TYPE_BY_EXTENSION[extensionOf(file.name)] ?? 'application/octet-stream',
        base64Data,
      });
    };
    reader.readAsDataURL(file);
  },
);

// The leading `!` is optional because an image is stored as an image link, `![alt](url)`, while a
// document is stored as a plain one. Both are the same thing to a restored bubble: one attachment.
const ATTACHMENT_PREFIX_PATTERN = /^\s*!?\[([^\]]+)\]\(https:\/\/[^)]+\)\s*/;

/**
 * @description Split the markdown link the agents service prepends to a turn that carried a file,
 * so a restored message renders as a chip plus its text — the same way it looked when sent.
 *
 * Deliberately host-agnostic. The link points at whatever `GCS_STORAGE_URL` is set to in the
 * agents deployment — `https://cdn.neo.gent/` on production, a bucket URL elsewhere — so pinning
 * a host here would silently stop stripping the moment that config changes. This chat never sends
 * markdown, so a human turn cannot legitimately begin with a link.
 *
 * The label is whatever the service put in the link text. That is the filename for a document, and
 * for an image either the filename or, for a turn stored before images carried one, the literal
 * "uploaded image" — a picture archived under a content hash has no filename anywhere else, so
 * there is nothing better to fall back to.
 */
export const splitAttachmentPrefix = (content: string): { attachmentName: string | null; text: string } => {
  const match = ATTACHMENT_PREFIX_PATTERN.exec(content);
  if (!match) return { attachmentName: null, text: content };
  return { attachmentName: match[1], text: content.slice(match[0].length).trim() };
};
